import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, CreditCard, Lock, ShieldCheck, XCircle } from 'lucide-react';
import {
  fetchPaymentOptions,
  paymentKeys,
  startPayment,
  verifyPayment,
  type PaymentMethod,
} from '@/api/payments';
import { orderKeys } from '@/api/orders';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { PaymentPanelSkeleton } from '@/components/ui/Skeleton';
import { ApiException } from '@/types/api';

/**
 * Paying for a placed order — BUILD-PLAN §25, §26, §86.
 *
 * Shown after the order exists, not before. That ordering is deliberate: the
 * order is the record of what the customer agreed to, and a payment that
 * cannot be tied back to one is a payment nobody can reconcile or refund.
 *
 * Every figure comes from the server. The panel never computes a total, never
 * converts a currency, and never decides which gateway to offer — it renders
 * what `/checkout/orders/{n}/payment` says is possible (§29, §86).
 */
export function PaymentPanel({
  orderNumber,
  checkoutToken,
}: {
  orderNumber: string;
  checkoutToken: string | null;
}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * How the customer got here.
   *
   * `complete` means the gateway sent them back saying they paid; `cancelled`
   * means they backed out. Neither is proof — the gateway tells the CUSTOMER
   * one thing and tells US another, by webhook, and only the webhook is
   * authoritative (§27). This decides what to show while we wait for it.
   */
  const returned = searchParams.get('payment');

  const [method, setMethod] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set once a non-redirect method has been started successfully. */
  const [started, setStarted] = useState<string | null>(null);

  /*
   * One key per attempt, generated once and kept in a ref.
   *
   * §26 — a double-click, a slow network or a re-render must not become a
   * second charge. Regenerating this on render would defeat the whole point
   * of the server's idempotency, which is keyed on exactly this value.
   */
  const idempotencyKey = useRef(
    `pay-${orderNumber}-${Math.random().toString(36).slice(2, 10)}`,
  );

  const { data, isPending, isError } = useQuery({
    queryKey: paymentKeys.options(orderNumber),
    queryFn: () => fetchPaymentOptions(orderNumber, checkoutToken ?? ''),
    enabled: Boolean(checkoutToken),
    retry: false,

    /*
     | Poll only while waiting for a webhook we are expecting.
     |
     | A customer returning from a gateway arrives BEFORE the webhook does —
     | the redirect is a browser hop, the webhook is a server call, and they
     | race. Without this the page shows "pay for your order" to someone who
     | has just paid, which is the most alarming thing a checkout can do.
     |
     | Bounded to a few seconds' cadence and stopped the moment it settles, so
     | an unpaid order is not polled for ever.
     */
    refetchInterval: (query) =>
      returned === 'complete' && query.state.data?.paymentStatus !== 'paid' ? 2500 : false,
  });

  /*
   | Ask the server to CHECK WITH THE PROVIDER on return.
   |
   | Polling the options endpoint alone only re-reads our own database, so it
   | can never settle anything on its own — it just waits for a webhook that
   | may be delayed, lost, or (against a `.test` host in development)
   | impossible to deliver. Every poll now also asks the backend to query the
   | gateway directly, which is what actually converts a real payment into a
   | confirmed order.
   |
   | Runs only while returning and unsettled, so a customer sitting on an
   | unpaid order is not making outbound calls to a payment provider for ever.
   */
  useEffect(() => {
    if (returned !== 'complete' || !checkoutToken) return;
    if (data?.paymentStatus === 'paid') return;

    let cancelled = false;

    const check = async () => {
      try {
        const result = await verifyPayment(orderNumber, checkoutToken);

        if (!cancelled && result.paid) {
          await queryClient.invalidateQueries({ queryKey: paymentKeys.options(orderNumber) });
          await queryClient.invalidateQueries({ queryKey: orderKeys.order(orderNumber) });
        }
      } catch {
        // A failed check is not a failed payment — the next poll tries again.
      }
    };

    void check();

    const timer = window.setInterval(() => void check(), 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [returned, checkoutToken, orderNumber, data?.paymentStatus, queryClient]);

  const methods = data?.methods ?? [];

  /* The amount follows the CHOSEN method, since gateways settle in
     different currencies. Falls back to the order's own figure. */
  const chosen = methods.find((option) => option.key === method);
  const due = chosen?.amount ?? data?.amount;

  // Default to the first offered method, so a single option needs no choosing.
  useEffect(() => {
    if (!method && methods.length > 0) setMethod(methods[0]!.key);
  }, [methods, method]);

  /*
   * Without a token there is nothing to authorise with — the customer has
   * arrived here on a refresh, which discards navigation state. Said plainly
   * rather than showing a pay button that cannot work (§86).
   */
  if (!checkoutToken) {
    return (
      <Alert tone="info">
        We will be in touch shortly to arrange payment for this order.
      </Alert>
    );
  }

  /*
   * A shaped placeholder, not a line of text.
   *
   * This is the most anxious moment in the journey — the order is placed and
   * the customer is waiting to be told how to pay. A bare "Loading…" there
   * reads as something having gone wrong, and the panel then appears from
   * nowhere and shifts the page (§41).
   */
  if (isPending) {
    return <PaymentPanelSkeleton />;
  }

  if (isError || !data) {
    return (
      <Alert tone="info">
        We could not load payment options just now. We will contact you to arrange payment.
      </Alert>
    );
  }

  /*
   * Started, with nothing to redirect to. The customer needs to know it
   * worked and what happens next — not to be dropped back on the same form.
   */
  if (started) {
    return (
      <Alert tone="success">
        <span className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span>
            {started} We have emailed your order reference; quote it when you pay.
          </span>
        </span>
      </Alert>
    );
  }

  /*
   * Back from the gateway saying it worked, but the webhook has not landed.
   * Said honestly: we are confirming, not confirmed. Claiming success on the
   * strength of a redirect would mark orders paid that never cleared.
   */
  if (returned === 'complete' && data.paymentStatus !== 'paid') {
    return (
      <Alert tone="info">
        <span className="flex items-start gap-2">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span>
            Thank you — we are confirming your payment with the provider. This usually takes a
            few seconds, and we will email you once it clears.
          </span>
        </span>
      </Alert>
    );
  }

  if (data.paymentStatus === 'paid') {
    return (
      <Alert tone="success">
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          Payment received — thank you.
        </span>
      </Alert>
    );
  }

  /*
   * Backed out at the gateway. Not an error, and not a dead end: the order
   * still stands and they can choose again. Clearing the flag first so the
   * notice does not survive the next attempt.
   */
  const cancelled = returned === 'cancelled' && data.paymentStatus !== 'paid';

  /*
   * No gateway can take this order. Not a failure to hide: the customer is
   * told how it will be settled instead of being shown a dead button (§86).
   */
  if (methods.length === 0) {
    return (
      <Alert tone="info">
        Online payment is not available for this order. We will contact you to arrange payment
        before it is dispatched.
      </Alert>
    );
  }

  async function pay() {
    setError(null);
    setSubmitting(true);

    /*
     | Clear the return flag before starting again, so a "cancelled" notice
     | does not linger over a new attempt.
     */
    if (returned) {
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }

    try {
      const attempt = await startPayment(
        orderNumber,
        checkoutToken ?? '',
        method,
        idempotencyKey.current,
      );

      if (attempt.redirectUrl) {
        /*
         | The gateway hosts the payment page, so the customer leaves the
         | site. `replace` rather than `assign`: returning here by the back
         | button, mid-payment, would show a stale panel offering to start a
         | payment that is already under way.
         */
        window.location.replace(attempt.redirectUrl);

        return;
      }

      /*
       | No redirect — an offline method, where nothing is hosted anywhere.
       |
       | This used to reload the page, which returned the customer to exactly
       | what they were looking at with no sign anything had happened. They
       | reasonably concluded the button was broken. The instructions are
       | shown instead (§86).
       */
      setStarted(attempt.message);
      setSubmitting(false);
    } catch (exception) {
      setError(
        exception instanceof ApiException
          ? exception.message
          : 'We could not start your payment. Please try again.',
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="border border-line bg-surface p-6 sm:p-7">
      <h2 className="flex items-center gap-2 font-display text-xl text-ink">
        <CreditCard className="h-4 w-4 text-accent" strokeWidth={1.5} aria-hidden="true" />
        Pay for your order
      </h2>

      <p className="mt-2 text-sm leading-relaxed text-muted">
        <span className="text-ink">{due?.formatted}</span> due
        {/* §56 — when the charge currency differs from the order's, both are
            shown. A customer must never be surprised by the figure on their
            statement, and with a converting gateway that figure is not the
            one they were quoted in the shop. */}
        {chosen?.converted && (
          <> — {data.baseAmount.formatted} converted at today&apos;s rate.</>
        )}
        {!chosen?.converted && '.'}
      </p>

      {cancelled && (
        <div className="mt-4">
          <Alert tone="info">
            <span className="flex items-start gap-2">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <span>
                Payment was not completed. Your order is still held — choose a method below to
                try again.
              </span>
            </span>
          </Alert>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {methods.length > 1 && (
        <div role="radiogroup" aria-label="Payment method" className="mt-5 flex flex-col gap-3">
          {methods.map((option: PaymentMethod) => (
            <label
              key={option.key}
              className={`flex cursor-pointer items-center gap-3 border px-5 py-4 transition-colors ${
                method === option.key ? 'border-accent' : 'border-line hover:border-ink'
              }`}
            >
              <input
                type="radio"
                name="payment_method"
                value={option.key}
                checked={method === option.key}
                onChange={() => setMethod(option.key)}
                className="h-4 w-4 accent-accent"
              />
              <span className="flex flex-1 flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-sm text-ink">{option.label}</span>
                <span className="text-sm text-ink">
                  {option.amount.formatted}
                  {option.converted && (
                    <span className="ml-1 text-xs text-muted">({option.currency})</span>
                  )}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="mt-6">
        <Button
          type="button"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={!method}
          onClick={() => void pay()}
        >
          Pay {due?.formatted}
        </Button>
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted">
        <Lock className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={1.5} aria-hidden="true" />
        You will be taken to our payment provider to complete this securely. Your card details
        never reach our servers.
      </p>
    </div>
  );
}
